import React from 'react';
import { Users, Search, Filter, ChevronLeft, ChevronRight } from 'lucide-react';
import { Badge } from '../../../../components/ui/Badge';

export const EmployeePickerTable = ({
  employees,
  employeesToShow,
  totalItems,
  totalPages,
  page,
  setPage,
  pageSize,
  setPageSize,
  pageSizeOptions,
  rangeStart,
  rangeEnd,
  searchQuery,
  setSearchQuery,
  selectedEmployeeForRole,
  onEmployeeClick,
  onFiltersClick
}) => {
  return (
    <div className="rounded-2xl border border-gray-700 bg-gray-800 shadow-lg w-full min-w-0 h-full flex flex-col min-h-0 max-h-[700px]">
      <div className="px-4 sm:px-6 py-4 border-b border-gray-700 shrink-0">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3 flex-wrap">
            <Users className="w-5 h-5 text-gray-400 shrink-0" />
            <h2 className="text-xl font-semibold text-white">
              Listado de Empleados
            </h2>
            <Badge className="bg-blue-900/50 text-blue-200">
              {totalItems} {totalItems === 1 ? 'empleado' : 'empleados'}
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 px-3 py-2 bg-gray-700 rounded-lg border border-gray-600 w-full sm:w-auto min-w-[200px]">
              <Search className="w-4 h-4 text-gray-400 shrink-0" />
              <input
                type="text"
                placeholder="Buscar empleados..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="bg-transparent border-0 outline-0 text-white placeholder-gray-400 text-sm w-full"
              />
            </div>
            <button
              type="button"
              onClick={onFiltersClick}
              className="inline-flex items-center justify-center p-2 rounded-lg border border-blue-600 bg-blue-600 text-white hover:bg-blue-700 transition-colors"
              title="Filtros"
            >
              <Filter className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      <div className="p-4 sm:p-6 w-full min-w-0 flex-1 flex flex-col min-h-0">
        {employees.length === 0 ? (
          <div className="text-center py-16">
            <Users className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 text-lg">
              No hay empleados registrados.
            </p>
          </div>
        ) : (
          <>
            <div className="w-full flex-1 min-h-0 overflow-y-auto">
              <div className="inline-block w-full align-middle">
                <div className="shadow ring-1 ring-gray-700 rounded-lg w-full">
                  <table className="w-full divide-y divide-gray-700">
                    <thead className="sticky top-0 z-10 bg-gray-700/50 shadow-sm">
                      <tr>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                          Nombre Completo
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                          Base Fábrica
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                          Estado
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-gray-800 divide-y divide-gray-700">
                      {employeesToShow.map((employee, index) => (
                        <tr
                          key={employee.id}
                          onClick={() => onEmployeeClick(employee)}
                          className={`transition-colors ${
                            index % 2 === 0 ? 'bg-gray-800' : 'bg-gray-750'
                          } hover:bg-gray-700 cursor-pointer ${
                            selectedEmployeeForRole?.id === employee.id ? 'ring-2 ring-blue-500 ring-inset' : ''
                          }`}
                        >
                          <td className="px-6 py-4 min-w-0 max-w-0">
                            <div className="flex flex-col min-w-0 max-w-full">
                              {(() => {
                                const fullName = employee.first_name && employee.last_name
                                  ? `${employee.first_name} ${employee.last_name}`
                                  : employee.first_name || employee.last_name || 'N/A';
                                return (
                                  <span 
                                    className="text-sm font-medium text-white truncate min-w-0 max-w-full"
                                    title={fullName}
                                  >
                                    {fullName}
                                  </span>
                                );
                              })()}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <Badge className="bg-violet-900/50 text-violet-200">
                              {employee.factory_base || 'N/A'}
                            </Badge>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex flex-col gap-1.5">
                              <Badge
                                className={
                                  employee.active
                                    ? 'bg-emerald-900/50 text-emerald-200'
                                    : 'bg-red-900/50 text-red-200'
                                }
                              >
                                {employee.active ? 'Activo' : 'Inactivo'}
                              </Badge>
                              {employee.external && (
                                <Badge className="bg-blue-900/50 text-blue-200">
                                  Externo
                                </Badge>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {totalItems > 0 && (
              <div className="px-4 sm:px-6 py-4 border-t border-gray-700 flex flex-col sm:flex-row items-center justify-between gap-4 shrink-0">
                <div className="flex items-center gap-4 flex-wrap">
                  <p className="text-sm text-gray-500">
                    Mostrando {rangeStart}–{rangeEnd} de {totalItems} empleados
                  </p>
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-gray-400">Mostrar:</label>
                    <select
                      value={pageSize}
                      onChange={(e) => setPageSize(Number(e.target.value))}
                      className="px-3 py-1.5 rounded-lg border border-gray-600 bg-gray-800 text-gray-300 text-sm font-medium hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      {pageSizeOptions?.map((size) => (
                        <option key={size} value={size} className="bg-gray-800">
                          {size}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-gray-600 bg-gray-800 text-gray-300 text-sm font-medium hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Anterior
                  </button>
                  <span className="text-sm text-gray-500 px-2">
                    Página {page} de {totalPages}
                  </span>
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                    className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-gray-600 bg-gray-800 text-gray-300 text-sm font-medium hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Siguiente
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
